import axios from "axios";
import { API_URL } from "configs";

export const EMAIL_SETTINGS_PATHS = Object.freeze({
  templates: "appraisals/email-templates",
  templateById: (id) => `appraisals/email-templates/${id}`,
  reminders: "appraisals/email-reminders",
  reminderOptions: "appraisals/email-reminders/options",
  reminderById: (id) => `appraisals/email-reminders/${id}`,
  mailLogs: "appraisals/email-mail-logs",
  mailLogById: (id) => `appraisals/email-mail-logs/${id}`,
  smtpSettings: "appraisals/email-smtp-settings",
  smtpTest: "appraisals/email-smtp-settings/test",
});

export const QUEUE_MODE_OPTIONS = [
  { value: "queued", label: "Queued" },
  { value: "immediate", label: "Immediate" },
];

export const MAIL_LOG_STATUS_OPTIONS = [
  { value: "queued", label: "Queued" },
  { value: "sent", label: "Sent" },
  { value: "failed", label: "Failed" },
];

export const EVENT_CODE_OPTIONS = [
  { value: "paf.generation", label: "PAF Generation Email" },
  { value: "paf.scoring", label: "PAF Scoring Email" },
  { value: "paf.scoring_approval", label: "PAF Scoring & Approval Email" },
  { value: "paf.sent_to_rtm", label: "PAF Ready for Manager Review" },
  { value: "paf.approval_reminder", label: "PAF Approval Deadline Reminder" },
  { value: "paf.rejected", label: "PAF Returned to Supervisor" },
  { value: "paf.resubmitted", label: "PAF Resubmitted to Manager" },
  { value: "paf.approved", label: "PAF Approved" },
  { value: "smtp.test", label: "SMTP Test Email" },
];

export const DEFAULT_TEMPLATE_PLACEHOLDERS = [
  "recipient_name",
  "employee_name",
  "employee_email",
  "appraiser_name",
  "approver_name",
  "supervisor_name",
  "manager_name",
  "paf_type",
  "paf_title",
  "appraisal_form_name",
  "appraisal_type",
  "frequency",
  "status",
  "appraisal_date",
  "due_date",
  "supervisor_deadline",
  "manager_deadline",
  "deadline_date",
  "scoring_deadline",
  "approval_deadline",
  "rejection_reason",
  "appraisal_id",
  "appraisal_progress_id",
  "company_name",
];

export const ENCRYPTION_OPTIONS = [
  { value: "tls", label: "TLS" },
  { value: "ssl", label: "SSL" },
  { value: "", label: "None" },
];

const authHeaders = () => ({
  "Content-Type": "application/json",
  Accept: "application/json",
  Authorization: `Bearer ${localStorage.getItem("token")}`,
});

const withObject = (objectViewedId, payload = {}) =>
  objectViewedId ? { ...payload, object_viewed_id: objectViewedId } : payload;

/** Drops undefined/empty filter values so the API never receives blank query params. */
const compact = (filters = {}) =>
  Object.fromEntries(
    Object.entries(filters).filter(
      ([, value]) => value !== undefined && value !== null && value !== ""
    )
  );

export const fetchEmailTemplates = async (objectViewedId) => {
  const { data } = await axios.get(`${API_URL}${EMAIL_SETTINGS_PATHS.templates}`, {
    headers: authHeaders(),
    params: withObject(objectViewedId),
  });
  return Array.isArray(data) ? data : data?.data || [];
};

export const fetchEmailTemplate = async (id, objectViewedId) => {
  const { data } = await axios.get(`${API_URL}${EMAIL_SETTINGS_PATHS.templateById(id)}`, {
    headers: authHeaders(),
    params: withObject(objectViewedId),
  });
  return data?.data || data;
};

export const updateEmailTemplate = async (id, payload, objectViewedId) => {
  const { data } = await axios.put(
    `${API_URL}${EMAIL_SETTINGS_PATHS.templateById(id)}`,
    withObject(objectViewedId, payload),
    { headers: authHeaders() }
  );
  return data?.data || data;
};

export const fetchEmailReminders = async (objectViewedId) => {
  const { data } = await axios.get(`${API_URL}${EMAIL_SETTINGS_PATHS.reminders}`, {
    headers: authHeaders(),
    params: withObject(objectViewedId),
  });
  return Array.isArray(data) ? data : data?.data || [];
};

export const fetchEmailReminderOptions = async (objectViewedId) => {
  const { data } = await axios.get(`${API_URL}${EMAIL_SETTINGS_PATHS.reminderOptions}`, {
    headers: authHeaders(),
    params: withObject(objectViewedId),
  });
  return data?.data || data || {};
};

export const fetchEmailReminder = async (id, objectViewedId) => {
  const { data } = await axios.get(`${API_URL}${EMAIL_SETTINGS_PATHS.reminderById(id)}`, {
    headers: authHeaders(),
    params: withObject(objectViewedId),
  });
  return data?.data || data;
};

export const updateEmailReminder = async (id, payload, objectViewedId) => {
  const { data } = await axios.put(
    `${API_URL}${EMAIL_SETTINGS_PATHS.reminderById(id)}`,
    withObject(objectViewedId, payload),
    { headers: authHeaders() }
  );
  return data?.data || data;
};

export const createEmailReminder = async (payload, objectViewedId) => {
  const { data } = await axios.post(
    `${API_URL}${EMAIL_SETTINGS_PATHS.reminders}`,
    withObject(objectViewedId, payload),
    { headers: authHeaders() }
  );
  return data?.data || data;
};

export const deleteEmailReminder = async (id, objectViewedId) => {
  const { data } = await axios.delete(
    `${API_URL}${EMAIL_SETTINGS_PATHS.reminderById(id)}`,
    {
      headers: authHeaders(),
      params: withObject(objectViewedId),
    }
  );
  return data;
};

/**
 * @returns {{ rows: Array, meta: Object|null }} Paginated mail log rows plus paginator meta.
 */
export const fetchEmailMailLogs = async (objectViewedId, filters = {}) => {
  const { data } = await axios.get(`${API_URL}${EMAIL_SETTINGS_PATHS.mailLogs}`, {
    headers: authHeaders(),
    params: withObject(objectViewedId, compact(filters)),
  });
  return {
    rows: Array.isArray(data) ? data : data?.data || [],
    meta: data?.meta || null,
  };
};

export const fetchEmailMailLog = async (id, objectViewedId) => {
  const { data } = await axios.get(`${API_URL}${EMAIL_SETTINGS_PATHS.mailLogById(id)}`, {
    headers: authHeaders(),
    params: withObject(objectViewedId),
  });
  return data?.data || data;
};

export const fetchEmailSmtpSettings = async (objectViewedId) => {
  const { data } = await axios.get(`${API_URL}${EMAIL_SETTINGS_PATHS.smtpSettings}`, {
    headers: authHeaders(),
    params: withObject(objectViewedId),
  });
  return data?.data || data;
};

export const updateEmailSmtpSettings = async (payload, objectViewedId) => {
  const { data } = await axios.put(
    `${API_URL}${EMAIL_SETTINGS_PATHS.smtpSettings}`,
    withObject(objectViewedId, payload),
    { headers: authHeaders() }
  );
  return data?.data || data;
};

/** The API expects the recipient under `to`. */
export const testEmailSmtpSettings = async ({ to_email: toEmail }, objectViewedId) => {
  const { data } = await axios.post(
    `${API_URL}${EMAIL_SETTINGS_PATHS.smtpTest}`,
    withObject(objectViewedId, { to: toEmail }),
    { headers: authHeaders() }
  );
  return data;
};

export const getErrorMessage = (error) => {
  const response = error?.response;
  if (response?.status === 422) {
    const errors = response.data?.errors;
    if (errors && typeof errors === "object") {
      const firstKey = Object.keys(errors)[0];
      const firstMessage = firstKey ? errors[firstKey]?.[0] : null;
      if (firstMessage) return String(firstMessage);
    }
    return response.data?.error || response.data?.message || "Validation failed.";
  }
  return (
    response?.data?.message ||
    response?.data?.error ||
    error?.message ||
    "Something went wrong."
  );
};
